import { FileQueryTable, getSortBy } from '@app/api/table.api';
import { Modal } from '@app/components/common/Modal/Modal';
import { fileColumns } from '@app/components/tables/FileTable/Column';
import { useFileTableData } from '@app/components/tables/FileTable/FileTable';
import { IFile } from '@app/interfaces/file';
import { TableRecordType } from '@app/types/generalTypes';
import { TablePaginationConfig } from 'antd';
import { FilterValue, SorterResult, TableRowSelection } from 'antd/lib/table/interface';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as S from '../../../tables/WorkerTable/WorkerTable.styles';
import { Table } from '../../Table/Table';
import { EditableCell } from '@app/components/tables/editableTable/EditableCell';
import { FilterIcon } from '../../icons/FilterIcon';

export interface ChooseFileModalProps {
  loading: boolean;
  isOpen: boolean;
  multiple: boolean;
  isImage?: boolean;
  onOpenChange: () => void;
  onSelectFiles: (file: IFile[]) => void;
}

export const ChooseFileModal: React.FC<ChooseFileModalProps> = ({
  loading,
  isOpen,
  multiple,
  isImage,
  onOpenChange,
  onSelectFiles,
}) => {
  const { t } = useTranslation();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<IFile[]>([]);
  const [initialPagination, setInitialPagination] = useState<FileQueryTable>({
    isImage,
    page: 1,
    limit: 10,
    search: '',
    sortBy: '_id:desc',
  });
  const [query, setQuery] = useState('');

  const { tableData, fetch } = useFileTableData(initialPagination);

  const onOk = () => {
    onSelectFiles(selectedFiles);
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<TableRecordType> | SorterResult<TableRecordType>[],
  ) => {
    const sortBy = Array.isArray(sorter) ? getSortBy(sorter[0]) : getSortBy(sorter);
    const newPagination = {
      page: pagination.current || 1,
      limit: pagination.pageSize || 10,
      search: query,
      sortBy: sortBy ? sortBy : '_id:desc',
    };
    setInitialPagination(newPagination);
    fetch(newPagination);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPagination = {
      ...initialPagination,
      search: event.target.value,
    };
    setQuery(event.target.value);
    setInitialPagination(newPagination);
    fetch(newPagination);
  };

  const columns = [
    ...fileColumns?.map((item) => {
      item.title = t(item.title);
      return item;
    }),
  ];

  const onSelectChange = (newSelectedRowKeys: React.Key[], selectedRows: IFile[]) => {
    if (!multiple) {
      setSelectedRowKeys(newSelectedRowKeys.slice(-1));
      setSelectedFiles(selectedRows.slice(-1));
      return;
    }
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedFiles(selectedRows);
  };

  const rowSelection: TableRowSelection<IFile> = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  useEffect(() => {
    fetch(initialPagination);
  }, [fetch, initialPagination]);

  return (
    <Modal
      title={t('common.chooseFiles')}
      size="large"
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      open={isOpen}
      onOk={onOk}
      onCancel={onOpenChange}
      destroyOnClose
      closable={false}
      confirmLoading={loading}
    >
      <S.TableActionWrapper className="flex-start">
        <S.InputSearch
          placeholder={t('header.search')}
          className="search-input"
          onChange={(event) => handleSearch(event)}
          filter={!query ? <S.Btn size="small" type={'text'} aria-label="Filter" icon={<FilterIcon />} /> : null}
          allowClear
          enterButton={null}
          addonAfter={null}
        />
      </S.TableActionWrapper>
      <Table
        components={{
          body: {
            cell: EditableCell,
          },
        }}
        bordered
        rowSelection={{ type: 'checkbox', ...rowSelection }}
        dataSource={tableData.data}
        columns={columns}
        rowClassName="editable-row"
        pagination={{
          current: tableData.pagination.page,
          pageSize: tableData.pagination.limit,
          total: tableData.pagination.total,
          locale: { items_per_page: `/ ${t('common.page')}` },
        }}
        onChange={handleTableChange}
        loading={tableData.loading}
        locale={{ emptyText: t('common.noData') }}
      />
    </Modal>
  );
};
